/* eslint-disable @typescript-eslint/no-use-before-define */
// Abstract base ↔ concrete subclass references below are intentional and safe
// under class hoisting — every reference site runs after both classes are fully
// initialized.
import { case as case_, int32, union, void as voidType } from "@stellar/js-xdr";
import { XdrError, type XdrType } from "@stellar/js-xdr";
import { XdrValue } from "../values/xdr-value.js";

export type FeeBumpTransactionExtWire = { v: 0 };

export type FeeBumpTransactionExtVariantName = "v0";

/**
 * ```xdr
 * union switch (int v)
 *     {
 *     case 0:
 *         void;
 *     }
 * ```
 */
abstract class FeeBumpTransactionExtBase extends XdrValue {
  abstract readonly type: FeeBumpTransactionExtVariantName;

  constructor() {
    super();
    // `new.target`, not an unconditional throw: every arm subclass reaches
    // this constructor through `super()`, void arms via an implicit one
    if (new.target === FeeBumpTransactionExtBase) {
      throw new TypeError(
        "new xdr.FeeBumpTransactionExt(...) is not supported: XDR unions are built from " +
          "per-variant factories. Call xdr.FeeBumpTransactionExt.v0() " +
          "(or another arm factory) instead.",
      );
    }
  }

  static readonly schema: XdrType<FeeBumpTransactionExtWire> = union(
    "FeeBumpTransactionExt",
    {
      switchOn: int32(),
      cases: [case_("v0", 0, voidType())],
      switchKey: "v",
    },
  );

  static v0(): FeeBumpTransactionExtV0 {
    return new FeeBumpTransactionExtV0();
  }

  static fromXdrObject(wire: FeeBumpTransactionExtWire): FeeBumpTransactionExt {
    switch (wire.v) {
      case 0:
        return new FeeBumpTransactionExtV0();
    }
    // unreachable for a well-typed wire object; a hand-built one can still
    // carry an out-of-range discriminant
    throw new XdrError(
      `FeeBumpTransactionExt: unknown v ${(wire as { v: unknown }).v}`,
    );
  }

  /**
   * Type guard narrowing an unknown value to a concrete FeeBumpTransactionExt variant.
   * Use this instead of `instanceof FeeBumpTransactionExt`: the exported `FeeBumpTransactionExt` value
   * is the abstract base, so `instanceof` narrows to the base (not the
   * variant union) and forces a cast. `FeeBumpTransactionExt.is(x)` narrows to the union.
   */
  static is(value: unknown): value is FeeBumpTransactionExt {
    return value instanceof FeeBumpTransactionExtBase;
  }

  abstract toXdrObject(): FeeBumpTransactionExtWire;
}

export class FeeBumpTransactionExtV0 extends FeeBumpTransactionExtBase {
  readonly type = "v0" as const;

  get value(): null {
    return null;
  }

  toXdrObject(): Extract<FeeBumpTransactionExtWire, { v: 0 }> {
    return { v: 0 };
  }
}

export type FeeBumpTransactionExt = FeeBumpTransactionExtV0;
export const FeeBumpTransactionExt = FeeBumpTransactionExtBase;
